import React, { useState, useEffect, useRef } from 'react';
import { Layout, Row, Col, Menu, Dropdown, Divider, Badge, Avatar, Tooltip } from 'antd';
import { graphql, useQuery, useMutation } from 'react-apollo';
import compose from 'lodash/flowRight';
import { withRouter } from 'react-router-dom';
import AvatarS3 from '../../shared/images/AvatarS3';
import { Auth } from 'aws-amplify';
import hybridStorage from '../../../../utils/hybridStorage';
import CURRENT_USER from '../../../core/GraphQl/Queries/CURRENT_USER';
import SIGNOUT from '../../../core/GraphQl/Mutations/SIGNOUT';
import DELETE_NOTIFICATION from '../../../core/GraphQl/Mutations/DELETE_NOTIFICATION';
import DELETE_MANY_NOTIFICATIONS from '../../../core/GraphQl/Mutations/DELETE_MANY_NOTIFICATIONS';
import DELETE_MANY_NOTIFICATIONS_BY_USER from '../../../core/GraphQl/Mutations/DELETE_MANY_NOTIFICATIONS_BY_USER';
import GET_NOTIFICATIONS from '../../../core/GraphQl/Queries/GET_NOTIFICATIONS';
import Breadcrumb from './components/Breadcrumb';
import { isEmpty } from 'lodash';
import {
  BellOutlined,
  DownOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  RightCircleOutlined,
  UserOutlined,
  CloseCircleOutlined,
  DollarOutlined,
  AppstoreOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { HasAccess } from '@permify/react-role';

const style = {
  emptyMenu: {
    background: 'white',
    height: '40px',
    width: '200px',
    textAlign: 'center',
    padding: '10px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
  },
};
const AppHeader = ({
  toggleSideBar,
  state: { collapsed },
  currentUser,
  history,
  location,
  showMobileNavigation,
}) => {
  const { id, username, avatar, permission } = currentUser;
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth >= 768 && window.innerWidth <= 1024);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsTablet(window.innerWidth >= 768 && window.innerWidth <= 1200);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  let billing = permission.billing;

  const [isloading, setIsLoading] = useState(false);

  // Only keep one page of notifications in the browser at a time; the rest are
  // loaded on demand via "Load more" so we never store the full history.
  const NOTIFICATIONS_PAGE_SIZE = 10;

  // The webapp GraphQL schema exposes `notifications(...)` but no
  // `notificationsConnection`, so we can't cheaply query a total count. Instead
  // we over-fetch one extra record (see `first` below): if the server returns
  // more than we intend to display, at least one more page exists. This avoids
  // the false "Load more" when the total is an exact multiple of the page size.
  const [visibleCount, setVisibleCount] = useState(NOTIFICATIONS_PAGE_SIZE);

  // Control the dropdown so clicking "Load more" expands the list instead of
  // closing the menu (Ant's Dropdown auto-closes on any Menu.Item click).
  const [notificationsVisible, setNotificationsVisible] = useState(false);
  const keepNotificationsOpenRef = useRef(false);

  const handleNotificationsVisibleChange = (flag) => {
    if (!flag && keepNotificationsOpenRef.current) {
      keepNotificationsOpenRef.current = false;
      return; // ignore the close triggered by "Load more"
    }
    setNotificationsVisible(flag);
  };

  const { data, refetch: refetchNotifications } = useQuery(GET_NOTIFICATIONS, {
    variables: {
      userId: id,
      // Fetch one more than we display so we can detect whether more pages
      // exist without a separate count query.
      first: visibleCount + 1,
      skip: 0,
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const refreshNotifications = () => {
    refetchNotifications();
  };

  const [deleteNotification] = useMutation(DELETE_NOTIFICATION, {
    onCompleted: () => {
      setIsLoading(false);
      refreshNotifications();
    },
  });

  const [deleteManyNotifications] = useMutation(DELETE_MANY_NOTIFICATIONS, {
    onCompleted: () => {
      setIsLoading(false);
      refreshNotifications();
    },
  });

  const [deleteManyNotificationsByUser] = useMutation(DELETE_MANY_NOTIFICATIONS_BY_USER, {
    onCompleted: () => {
      setIsLoading(false);
      refreshNotifications();
    },
  });

  // Keep showing the last loaded list while a larger page is loading so the
  // dropdown doesn't flash its empty state when expanding via "Load more".
  const lastNotificationsRef = useRef([]);
  if (data && data.notifications) {
    lastNotificationsRef.current = data.notifications;
  }
  const fetchedNotifications =
    data && data.notifications ? data.notifications : lastNotificationsRef.current;

  // The extra record we over-fetched means there is at least one more page.
  const hasMoreNotifications = fetchedNotifications.length > visibleCount;
  const getNotification = fetchedNotifications.slice(0, visibleCount);

  const loadMoreNotifications = () => {
    setVisibleCount((count) => count + NOTIFICATIONS_PAGE_SIZE);
  };

  // Delete every notification for this user server-side so we don't have to pull
  // the whole list into the browser just to clear it.
  const clearNotification = () => {
    if (!isEmpty(getNotification)) {
      deleteManyNotificationsByUser({
        variables: {
          userId: id,
        },
      });
    }
  };

  const userMenuItems = [
    {
      key: 'setting:1',
      icon: <UserOutlined />,
      label: <Tooltip title="User Profile">{username}</Tooltip>,
      onClick: () => history.push('/account'),
    },
    ...(billing ? [{
      key: 'setting:2',
      icon: <DollarOutlined />,
      label: <Tooltip title="Billing">Billing</Tooltip>,
      onClick: () => history.push('/payment'),
    }] : []),
    {
      key: 'setting:4',
      icon: <LogoutOutlined />,
      label: <Tooltip title="Logout">Sign Out</Tooltip>,
      onClick: async () => {
        try {
          hybridStorage.clearPrimarySession();
          await Auth.signOut();
          console.log('✅ Logout successful');
          if (window && window.$chatwoot) {
            window.$chatwoot.reset();
          }
          window.location.href = '/';
        } catch (error) {
          console.error('❌ Logout failed:', error);
          window.location.href = '/';
        }
      },
    },
  ];

  const notificationMenu = isEmpty(getNotification) ? (
    <Menu>
      <Menu.Item>
        <Row>
          <Col span={2}>
            <CheckCircleOutlined style={{ color: 'green' }} />
          </Col>
          <Col offset={2} span={20}>
            You are all up to date!
          </Col>
        </Row>
      </Menu.Item>
    </Menu>
  ) : (
    <Menu>
      <Menu.Item
        key="clear-notifications"
        onClick={() => {
          clearNotification();
        }}
        style={{ backgroundColor: '#1890ff', color: 'white' }}
      >
        <Row>
          <Col span={2}>
            <CloseCircleOutlined />
          </Col>
          <Col offset={2} span={20}>
            Clear All Notifications
          </Col>
        </Row>
      </Menu.Item>
      {getNotification.map((notification, i) => {
        let redirectRoute = '';
        if (notification.type) {
          const type = notification.type.toLowerCase();
          switch (type) {
            case 'task_management':
              redirectRoute = '/tasks';
              break;
            // Add more cases if feature name is different to route name
            default:
              redirectRoute = `/${type}`;
              break;
          }
          if (notification.featureID) {
            redirectRoute += `/details/${notification.featureID}`;
          }
        }

        // No blank notification message allowed.
        if (!notification.message) return false;

        return (
          <Menu.Item key={`notification:${i}`} disabled={isloading}>
            <div
              style={{
                height: '100%',
                width: '100%',
                overflow: 'wrap',
                whiteSpace: 'normal',
              }}
              onClick={() => {
                setIsLoading(true);

                const getPath = history.location.pathname + history.location.search;
                history.push(getPath);

                // Delete all notifications with the same featureID
                if (notification.featureID) {
                  const relatedNotificationIds = getNotification
                    .filter((n) => n.featureID === notification.featureID)
                    .map((n) => n.id);

                  deleteManyNotifications({
                    variables: {
                      ids: relatedNotificationIds,
                    },
                  });
                } else {
                  // If no featureID, delete only the single notification
                  deleteNotification({
                    variables: {
                      notificationID: notification.id,
                    },
                  });
                }

                if (redirectRoute) history.push(redirectRoute);
              }}
            >
              <Row>
                <Col span={2}>
                  <RightCircleOutlined />
                </Col>
                <Col offset={2} span={20}>
                  {notification.message}
                </Col>
              </Row>
            </div>
          </Menu.Item>
        );
      })}
      {hasMoreNotifications && (
        <Menu.Item
          key="load-more-notifications"
          onClick={(e) => {
            if (e && e.domEvent) e.domEvent.stopPropagation();
            keepNotificationsOpenRef.current = true;
            loadMoreNotifications();
          }}
          style={{ textAlign: 'center', color: '#1890ff' }}
        >
          Load more
        </Menu.Item>
      )}
    </Menu>
  );

  // TODO: When Menu is collapsed need to readjust when order changes
  return (
    <Layout.Header
      style={{
        background: 'white',
        height: '100%',
        position: 'sticky',
        top: 0,
        zIndex: 10,
      }}
    >
      <Row type="flex" align="middle" style={{ position: 'relative' }}>
        <Col>
          {isMobile ? (
            <AppstoreOutlined onClick={showMobileNavigation} style={{ fontSize: '18px' }} />
          ) : !isTablet ? (
            collapsed ? (
              <MenuFoldOutlined onClick={toggleSideBar} />
            ) : (
              <MenuFoldOutlined onClick={toggleSideBar} />
            )
          ) : null}
          <Divider type="vertical" style={{ marginLeft: '20px', height: '36px' }} />
        </Col>
        <Col>
          <HasAccess permissions={['default']}>
            <Breadcrumb location={location} />
          </HasAccess>
        </Col>
        <HasAccess permissions={['default']}>
          <Col style={{ position: 'absolute', right: 0, top: 0 }}>
            <Row gutter={26} type={'flex'}>
              <Col>
                <Dropdown
                  overlay={notificationMenu}
                  trigger={['click']}
                  visible={notificationsVisible}
                  onVisibleChange={handleNotificationsVisibleChange}
                  placement="bottom"
                  align={{
                    offset: [0, 20],
                  }}
                  getPopupContainer={(triggerNode) => triggerNode.parentNode || document.body}
                  overlayStyle={{
                    ...(isEmpty(getNotification) ? {} : { overflowY: 'scroll' }),
                    maxHeight: '90vh',
                    width: '35vh',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                  }}
                >
                  <a className="ant-dropdown-link" href="#settings-dropdown">
                    <Badge
                      count={
                        hasMoreNotifications
                          ? `${getNotification.length}+`
                          : getNotification.length
                      }
                    >
                      <BellOutlined
                        style={{
                          color: 'black',
                        }}
                      />
                    </Badge>
                  </a>
                </Dropdown>
              </Col>
              {/*<Col>
              <Icon
                style={{
                  color: "black",
                }}
                type="message"
              />
              </Col>*/}
              <Col>
                <Dropdown
                  menu={{ items: userMenuItems }}
                  trigger={['click']}
                  placement="bottom"
                  overlayStyle={{
                    paddingTop: '20px',
                    position: 'fixed',
                  }}
                >
                  <a className="ant-dropdown-link" href="#settings-dropdown">
                    {avatar ? (
                      <AvatarS3 imgKey={avatar.key} avatarProps={{ size: 'medium' }} />
                    ) : (
                      <Avatar size="medium">{username.substring(0, 1)}</Avatar>
                    )}
                    <DownOutlined style={{ padding: '3px', color: '#636363' }} />
                  </a>
                </Dropdown>
              </Col>
            </Row>
          </Col>
        </HasAccess>
      </Row>
      <div
        style={{
          height: 1,
          width: '100%',
          background: '#E8E8E8',
          position: 'sticky',
          top: 64,
          zIndex: 2,
        }}
      />
    </Layout.Header>
  );
};

export default compose(
  graphql(CURRENT_USER, {
    props: ({ data: { currentUser } }) => {
      return { currentUser: currentUser };
    },
  }),
  graphql(SIGNOUT, { name: 'signOut' })
)(withRouter(AppHeader));
